/**
 * Display-only epoch progress above the metrics chart.
 *
 * Answers the one question the chart cannot: where inside the current epoch the
 * training loop is right now. The batch counts come from `StepMetrics` because
 * only the controller knows how many samples an epoch holds — deriving them
 * here would duplicate the dataset/batch-size arithmetic.
 *
 * Nothing in this module is interactive: training control and growth stay in
 * their own panes, so the bar never looks like a control.
 */
import type { StepMetrics } from '../engine/types.ts';

const NUMBER = new Intl.NumberFormat('en-US');

export class EpochProgress {
  #label: HTMLElement;
  #counts: HTMLElement;
  #track: HTMLElement;
  #bar: HTMLElement;

  constructor(label: HTMLElement, counts: HTMLElement, track: HTMLElement) {
    this.#label = label;
    this.#counts = counts;
    this.#track = track;

    const bar = track.querySelector<HTMLElement>('.progress-bar');
    if (!bar) {
      throw new Error('Epoch progress: missing .progress-bar');
    }
    this.#bar = bar;

    this.reset();
  }

  /** Called for every completed batch; the ratio is what fills the bar. */
  update({ epoch, epochStep, epochBatches }: StepMetrics): void {
    const percent = epochBatches > 0 ? Math.min(100, (epochStep / epochBatches) * 100) : 0;

    this.#label.textContent = `Epoch ${String(epoch)}`;
    this.#counts.textContent = `${NUMBER.format(epochStep)} / ${NUMBER.format(epochBatches)} batches`;
    this.#bar.style.width = `${percent.toFixed(2)}%`;
    this.#track.setAttribute('aria-valuenow', percent.toFixed(0));
    this.#track.setAttribute(
      'aria-valuetext',
      `Epoch ${String(epoch)}, batch ${String(epochStep)} of ${String(epochBatches)}`,
    );
  }

  /** No model yet, or the model was discarded: back to the pre-training state. */
  reset(): void {
    this.#label.textContent = 'Epoch -';
    this.#counts.textContent = '- / - batches';
    this.#bar.style.width = '0%';
    this.#track.setAttribute('aria-valuenow', '0');
    this.#track.removeAttribute('aria-valuetext');
  }
}
